import { t } from "../i18n/index.js";
/**
 * Fuerza experiencia horizontal en móvil: overlay en portrait + Screen
 * Orientation API cuando el navegador lo permite (Android / PWA fullscreen).
 */
export function createOrientationGate(root: HTMLElement = document.body): {
  dispose(): void;
  tryLock(): Promise<void>;
} {
  const overlay = document.createElement("div");
  overlay.className = "golazo-orientation-gate";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-label", t("Gira el dispositivo"));
  overlay.innerHTML = `
    <div class="golazo-orientation-gate__card">
      <div class="golazo-orientation-gate__phone" aria-hidden="true"></div>
      <p class="golazo-orientation-gate__title">${t("Gira el móvil")}</p>
      <p class="golazo-orientation-gate__hint">${t("FYNTA se juega en horizontal")}</p>
    </div>
  `;
  root.appendChild(overlay);

  /**
   * Que el dispositivo se maneje con el dedo, no con raton.
   *
   * El tamaño de la ventana NO sirve para esto: una ventana de escritorio
   * estrecha y alta cumple `portrait` y `max-width: 900px`, y salia el cartel
   * de "gira el movil" en un ordenador. `pointer: coarse` + `hover: none` es
   * la pareja que distingue de verdad tactil de raton — un portatil con
   * pantalla tactil sigue teniendo `hover: hover`, asi que no se le pide girar
   * nada.
   */
  const portraitQuery = window.matchMedia("(orientation: portrait)");
  const touchQuery = window.matchMedia("(pointer: coarse) and (hover: none)");
  const narrowQuery = window.matchMedia("(max-width: 900px)");

  const sync = (): void => {
    overlay.classList.toggle(
      "is-active",
      portraitQuery.matches && touchQuery.matches && narrowQuery.matches,
    );
  };

  sync();
  window.addEventListener("orientationchange", sync);
  portraitQuery.addEventListener("change", sync);
  touchQuery.addEventListener("change", sync);
  narrowQuery.addEventListener("change", sync);

  return {
    async tryLock(): Promise<void> {
      const o = screen.orientation as ScreenOrientation & {
        lock?: (orientation: string) => Promise<void>;
      };
      if (!o?.lock) return;
      try {
        await o.lock("landscape");
      } catch {
        // iOS Safari: solo en standalone/fullscreen; el overlay cubre portrait.
      }
    },
    dispose(): void {
      window.removeEventListener("orientationchange", sync);
      portraitQuery.removeEventListener("change", sync);
      touchQuery.removeEventListener("change", sync);
      narrowQuery.removeEventListener("change", sync);
      overlay.remove();
    },
  };
}
